import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  Box,
  Divider,
  CircularProgress,
} from "@mui/material";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import theme from "theme";
import { ReactComponent as SoundMasterLogo } from "../PlanSelect/SoundMasterLogo.svg";

type SoundMasterJoinConfirmDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
  soundMasterPrice?: number;
  campaignPrice?: number;
  campaignLabel?: string;
};

const SoundMasterJoinConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  loading = false,
  soundMasterPrice = 880,
  campaignPrice,
  campaignLabel, // 一旦、ラベルの内容は無視する
}: SoundMasterJoinConfirmDialogProps) => {
  const [t] = useTranslation();

  const effectivePrice = campaignPrice ?? soundMasterPrice;
  const totalPrice = 500 + effectivePrice;
  const hasCampaign = campaignPrice !== undefined;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogContent sx={{ pt: 4 }}>
        <LogoWrapper>
          <SoundMasterLogo role="img" aria-label="SoundMaster" />
        </LogoWrapper>

        <IntroText variant="body2" align="center" gutterBottom>
          {t("セッションがもっと盛り上がる！")}
        </IntroText>

        <PricingSection>
          <PriceBreakdownCard>
            <PriceRow>
              <Typography variant="body2" color="text.secondary">
                {t("現在のプラン（CCFOLIA PRO）")}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                ¥500/{t("月")}
              </Typography>
            </PriceRow>
            <PriceRow>
              <Typography variant="body2" color="text.secondary">
                {t("Sound Master")}
              </Typography>
              <Box sx={{ textAlign: "right" }}>
                {hasCampaign && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      textDecoration: "line-through",
                      display: "block",
                      lineHeight: 1.2,
                    }}
                  >
                    ¥{soundMasterPrice}/{t("月")}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  +¥{effectivePrice}/{t("月")}
                </Typography>
              </Box>
            </PriceRow>
            <Divider sx={{ my: 1.5 }} />
            <PriceRow>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                {t("お支払い合計")}
              </Typography>
              <Box sx={{ textAlign: "right" }}>
                {hasCampaign && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      textDecoration: "line-through",
                      display: "block",
                      lineHeight: 1.2,
                      mb: 0.5,
                    }}
                  >
                    ¥{500 + soundMasterPrice}/{t("月")}
                  </Typography>
                )}
                <Typography variant="h5">
                  ¥{totalPrice}
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ ml: 0.5, fontWeight: 500 }}
                  >
                    /{t("月")}
                  </Typography>
                </Typography>
                {/* 一旦、ラベルの内容は無視する */}
                {hasCampaign && campaignLabel && (
                  <Typography
                    variant="caption"
                    color="secondary"
                    align="center"
                    display="block"
                  >
                    {t("キャンペーン価格（初月のみ）")}
                  </Typography>
                )}
              </Box>
            </PriceRow>
          </PriceBreakdownCard>

          <NoticeBox>
            <Typography variant="caption" color="text.secondary">
              {t(
                "プラン切替時、CCFOLIA PRO の利用料金分は日割り計算で減額されます。"
              )}
            </Typography>
          </NoticeBox>
        </PricingSection>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button onClick={onClose} disabled={loading} sx={{ minWidth: 100 }}>
          {t("キャンセル")}
        </Button>
        <Button
          onClick={onConfirm}
          color="secondary"
          variant="contained"
          disabled={loading}
          fullWidth
          sx={{
            minWidth: 140,
            fontWeight: 600,
          }}
        >
          {loading ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} color="inherit" />
              {t("処理中...")}
            </Box>
          ) : (
            t("加入する")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const LogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 0 8px 0;
  svg {
    width: 180px;
    height: auto;
  }
`;

const IntroText = styled(Typography)`
  color: ${theme.palette.text.secondary};
  margin-bottom: 24px !important;
  font-weight: 500;
`;

const PricingSection = styled.div`
  margin-top: 8px;
`;

const PriceBreakdownCard = styled(Box)`
  background: ${theme.palette.mode === "dark"
    ? theme.palette.background.paper
    : theme.palette.background.default};
  border: 1px solid ${theme.palette.divider};
  border-radius: 8px;
  padding: 16px;
`;

const PriceRow = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
`;

const NoticeBox = styled(Box)`
  margin-top: 12px;
  padding: 8px 12px;
  background: ${theme.palette.mode === "dark"
    ? theme.palette.grey[900]
    : theme.palette.grey[100]};
  border-radius: 4px;
`;

export default memo(SoundMasterJoinConfirmDialog);
